import { useState } from 'react';
import { Form, Row, Col, Container, Button, Spinner, Modal } from 'react-bootstrap';
import { Image } from '../components/Image';
import { getCurrentUser, updatePassword } from 'aws-amplify/auth';

const allowedSpecialCharacters = '^ $ * . [ ] { } ( ) ? " ! @ # % & / \\ , > < \' : ; | _ ~ ` - + =';
const PASSWORD_HELP_TEXT = () => (
  <div>
    <p><b>Password Rules:</b></p>
    <ul style={{ listStyleType: 'circle' }}>
      <li>Must be at least eight characters in length.</li>
      <li>Cannot contain spaces.</li>
      <li>Must contain one uppercase character.</li>
      <li>Must contain one lowercase character.</li>
      <li>Must contain one number.</li>
      <li>Must contain one special character.</li>
      <li>Allowed special characters: {allowedSpecialCharacters}</li>
    </ul>
  </div>
);

// 8+ chars, 1 upper, 1 lower, 1 number, 1 special
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^$*.\[\]\\{}()?"!@#%&/\\,><':;|_+\-=~`])\S{8,99}$/;
const PASSWORDS_SAME_HELP_TEXT = 'The new password must be different from the current password';

function ChangePasswordForm(props) {
  const [existingPassword, setExistingPassword] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [isExistingPasswordValid, setIsExistingPasswordValid] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isConfirmedPasswordValid, setIsConfirmedPasswordValid] = useState(false);

  const [helpBlockText, setHelpBlockText] = useState(<PASSWORD_HELP_TEXT />);
  const [infoBlockText, setInfoBlockText] = useState('');
  const [submitError, setSubmitError] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const validateNew = (text) => PASSWORD_PATTERN.test(text);
  const validateExisting = (text) => text.trim().length > 0; // don't force old pw to match new policy

  const handleChangeExistingPassword = (e) => {
    const val = e.target.value;
    setExistingPassword(val);
    setIsExistingPasswordValid(validateExisting(val));
  };

  const handleChangePassword = (e) => {
    const val = e.target.value;
    setPassword(val);
    setIsPasswordValid(validateNew(val));
  };

  const handleChangeConfirmedPassword = (e) => {
    const val = e.target.value;
    setConfirmPassword(val);
    setIsConfirmedPasswordValid(validateNew(val));
  };

  const handleUpdatePassword = async (oldPw, newPw) => {
    try {
      await getCurrentUser(); // optional assertion
      await updatePassword({ oldPassword: oldPw, newPassword: newPw });
      setSubmitError(false);
      setHelpBlockText(<PASSWORD_HELP_TEXT />);
      setInfoBlockText('');
      setShowSuccessModal(true);
    } catch (e) {
      setSubmitError(true);
      setHelpBlockText(e?.message ?? 'Failed to update password.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isExistingPasswordValid || !isPasswordValid || !isConfirmedPasswordValid) return;
    if (password !== confirmPassword) return;

    if (password === existingPassword) {
      setInfoBlockText(PASSWORDS_SAME_HELP_TEXT);
      return;
    }

    setInfoBlockText('');
    setIsLoading(true);
    try {
      await handleUpdatePassword(existingPassword, password);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEnterPress = (e) => {
    if (e.key === 'Enter') {
      // let the form submit handle validation and blocking
      handleSubmit(e);
    }
  };

  const onAcknowledge = () => {
    setShowSuccessModal(false);
    if (props?.history?.push) props.history.push('/profile'); // v5 style
    // If you're on React Router v6, use useNavigate instead.
  };

  const textAreaErrorStyle = { border: '1px solid red', width: '100%', paddingLeft: '5px' };
  const textAreaStyle = { width: '100%', paddingLeft: '5px' };
  const helpTextStyle = { fontSize: 12 };
  const helpTextErrorStyle = { color: 'red', fontSize: 14 };

  return (
    <Container>
      <Image />
      <Row className="justify-content-md-center">
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col />
            <Col md="auto">
              <Form.Group controlId="existingPassword">
                <Form.Label>Existing Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Existing Password"
                  autoComplete="current-password"
                  onChange={handleChangeExistingPassword}
                  style={isExistingPasswordValid ? textAreaStyle : textAreaErrorStyle}
                />
              </Form.Group>
            </Col>
            <Col />
          </Row>

          <Row>
            <Col />
            <Col md="auto">
              <Form.Group controlId="newPassword">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="New Password"
                  autoComplete="new-password"
                  onChange={handleChangePassword}
                  style={isPasswordValid ? textAreaStyle : textAreaErrorStyle}
                />
              </Form.Group>
            </Col>
            <Col />
          </Row>

          <Row>
            <Col />
            <Col md="auto">
              <Form.Group controlId="confirmPassword">
                <Form.Label>Confirm Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Confirm Password"
                  autoComplete="new-password"
                  onChange={handleChangeConfirmedPassword}
                  onKeyDown={handleEnterPress}
                  style={isConfirmedPasswordValid ? textAreaStyle : textAreaErrorStyle}
                />
              </Form.Group>
            </Col>
            <Col />
          </Row>

          <Row>
            <Col />
            <Col md="auto">
              <Form.Text style={{ color: 'red' }}>{infoBlockText}</Form.Text>
              <div style={submitError ? helpTextErrorStyle : helpTextStyle}>{helpBlockText}</div>
            </Col>
            <Col />
          </Row>

          <Row>
            <Col />
            <Col md="auto">
              <Button
                variant="primary"
                size="sm"
                disabled={
                  !isExistingPasswordValid ||
                  !isPasswordValid ||
                  !isConfirmedPasswordValid ||
                  password !== confirmPassword ||
                  isLoading
                }
                type="submit"
              >
                {isLoading && (
                  <Spinner as="span" size="sm" animation="border" role="status" aria-hidden="true" />
                )}
                {!isLoading ? 'Submit' : ' Submitting....'}
              </Button>
            </Col>
            <Col />
          </Row>
        </Form>
      </Row>

      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        animation
        style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title className="w-100 text-center" style={{ fontSize: '16px', color: 'dimgray' }}>
            Password changed successfully.
          </Modal.Title>
        </Modal.Header>
        <Modal.Footer style={{ backgroundColor: 'rgb(0,168,168)' }}>
          <Button variant="outline-light" size="sm" onClick={onAcknowledge}>
            Ok
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default ChangePasswordForm;
